import Button from "components/Button";
import Layout from "components/Layout";
import Modal from "components/Modal";
import Or from "components/Or";
import Trainers from "components/Trainers";
import { jsonTrainers } from "utils/json";
import { ChangeEvent, FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { NotificationManager } from "react-notifications";
import RosterContext from "context/roster.context";
import Checkbox from "components/Checkbox";
import ITrainer from "types/Trainer";
import Utils from "utils";
import { firestore, useFirestore, useUser } from "reactfire";

type Props = {};

const ImportModal = styled(Modal)`
  text-align: center;

  > div {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  textarea {
    width: 50vw;
    min-height: 200px;
  }
`;

const placeholder = `{
  "Roy":3,
  "Rachel":1,
  "Michaella":3,
  "Tsubaki":2,
  "Luther":1,
  "Britra":4,
  "Helga":3,
  "Kai":2
}`;

const RosterPage: FunctionComponent<Props> = ({}) => {
  const { published, roster, setRoster, updateDate } = useContext(RosterContext);

  const user = useUser();

  const [trainers, setTrainers] = useState<ITrainer[]>(jsonTrainers);
  const [selected, setSelected] = useState<ITrainer[]>(roster);
  const [checked, setChecked] = useState(published);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importValue, setImportValue] = useState("");

  const [exportModalOpen, setExportModalOpen] = useState(false);

  const store = useFirestore();

  const exportValue = useMemo(() => {
    return roster.reduce((accum, trainer) => {
      accum[trainer.name] = trainer.level;
      return accum;
    }, {} as any);
  }, [updateDate]);

  useEffect(() => {
    setTrainers(Utils.updateTrainers(trainers, roster));
    setSelected(roster);
  }, [updateDate]);

  useEffect(() => {
    setChecked(published);
  }, [published]);

  const onImportChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setImportValue(e.target.value);
  };

  const onImportClick = () => {
    try {
      const value = JSON.parse(importValue);

      const roster: any[] = [];

      Object.entries(value).forEach(([name, level]) => {
        const trainer = jsonTrainers.find((t) => t.name === name);

        if (!trainer) {
          return;
        }

        roster.push({
          ...trainer,
          level,
        });
      });

      setRoster(roster);

      NotificationManager.success("Roster updated!");
      setImportModalOpen(false);
    } catch (e) {
      console.log(e);
      NotificationManager.error("Invalid JSON. Make sure you copied the file correctly");
    }
  };

  const onUpdateRoster = () => {
    setRoster(selected);
    NotificationManager.success("Roster saved!");
  };

  const onPublish = async (value: boolean) => {
    await store.collection("rosters").doc(user.data?.uid).update({ published: value });

    if (value) {
      NotificationManager.success("Set roster as public!");
    } else {
      NotificationManager.success("Set roster as private!");
    }

    setChecked(value);
  };

  const _Trainers = (
    <Trainers trainers={trainers} setTrainers={setTrainers} selected={selected} setSelected={setSelected} />
  );

  const getRosterUrl = () => {
    if (typeof window !== "undefined") {
      return `${window.origin}/roster/${user.data?.uid}`;
    }
  };

  const onCopy = () => {
    const el = document.createElement("textarea");
    el.value = getRosterUrl() as any;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    NotificationManager.info("Copied roster url");
  };

  const onExportCopy = () => {
    const el = document.createElement("textarea");
    el.value = JSON.stringify(exportValue);
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    NotificationManager.info("Copied roster json");
  };

  const onExportDownload = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportValue));
    const dlAnchorElem = document.getElementById("downloadAnchorElem") as HTMLElement;
    dlAnchorElem.setAttribute("href", dataStr);
    dlAnchorElem.setAttribute("download", "roster.json");
    dlAnchorElem.click();
  };

  return (
    <Layout>
      {/* <Head>
        <meta property="og:title" content="Roster | Deck Generator Baseball Superstars 2020"></meta>
        <meta property="og:description" content="Save your Baseball Superstars 2020's Roster"></meta>
        <title>Roster | Deck Generator Baseball Superstars 2020</title>
      </Head> */}

      {importModalOpen && (
        <ImportModal onClose={setImportModalOpen.bind(null, false)}>
          <>
            <div>Paste the JSON file content here</div>
            <div>
              <textarea placeholder={placeholder} onChange={onImportChange} value={importValue}></textarea>
            </div>
            <div>
              <Button disabled={importValue.length < 1} onClick={onImportClick}>
                Import
              </Button>
            </div>
          </>
        </ImportModal>
      )}

      {exportModalOpen && (
        <ImportModal onClose={setExportModalOpen.bind(null, false)}>
          <>
            <div>
              <textarea placeholder={placeholder} value={JSON.stringify(exportValue)}></textarea>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ marginRight: "16px" }}>
                <Button onClick={onExportCopy}>Copy</Button>
              </div>
              <div>
                <a id="downloadAnchorElem" style={{ display: "none" }}></a>
                <Button onClick={onExportDownload}>Download</Button>
              </div>
            </div>
          </>
        </ImportModal>
      )}

      <div
        style={{
          textAlign: "center",
          padding: "32px",
          height: "100%",
          overflow: "scroll",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ marginRight: "16px" }}>
            <Button active onClick={setImportModalOpen.bind(null, true)}>
              Import Roster
            </Button>
          </div>

          <div>
            <Button active onClick={setExportModalOpen.bind(null, true)}>
              Export Roster
            </Button>
          </div>
        </div>
        <Or />

        <div style={{ marginBottom: "32px" }}>
          <Button active={selected.length > 6} disabled={selected.length <= 6} onClick={onUpdateRoster}>
            Save Roster ({selected.length} trainers)
          </Button>
        </div>

        {user.data && (
          <Share>
            <Button disabled={!checked} onClick={onCopy} className="clipboard">
              Copy Roster URL
            </Button>

            <Checkbox checked={checked} onChecked={onPublish} label="Anybody can view it" />
          </Share>
        )}

        <div>
          <div style={{ flex: "1", minHeight: "0" }}>{_Trainers}</div>
        </div>
      </div>
    </Layout>
  );
};

const Share = styled.div`
  margin-bottom: 16px;

  > * {
    margin-right: 16px;
  }
`;

export default RosterPage;
